import React, { useState } from "react";
import { Pagination, EffectCoverflow, Navigation, Grid } from 'swiper/modules';
import 'swiper/css/grid';
import { Swiper, SwiperSlide } from 'swiper/react';
import { speakers } from "../../static/content";
import { faPlay, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

const [taylor, ...rest] = speakers;
const shuffled = [...rest].sort(() => Math.random() - 0.5);
const orderedSpeakers = [taylor, ...shuffled];

const isOrganizer = (name: string) => name === 'Taylor Oshan' || name === 'Yiqun Xie';
const isVolunteer = (speaker: any) => !!speaker.volunteer;

export default function Speakers(): JSX.Element {
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeVideo, setActiveVideo] = useState<{ name: string; url: string } | null>(null);

    const openPaper = (paper?: string) => {
        if (paper && paper !== 'disabled') window.open(paper, '_blank');
    };

    const renderActions = (speaker: any, isActive = true) => {
        if (!isActive) return null;

        const showPaper = speaker.paper && !isOrganizer(speaker.name) && !isVolunteer(speaker);
        const showVideo = speaker.video && !isVolunteer(speaker);
        const showBadge = isOrganizer(speaker.name) || isVolunteer(speaker);

        if (!showPaper && !showVideo && !showBadge) return null;

        return (
            <div className="paper-btn mt-auto mb-3 flex items-center justify-center gap-2">
                {isOrganizer(speaker.name) ? <span className="px-4 py-1 rounded-3xl border border-[#039876] text-[#039876] text-sm">Organizer</span> : null}
                {isVolunteer(speaker) ? <span className="px-4 py-1 rounded-3xl border border-[#039876] text-[#039876] text-sm">Volunteer</span> : null}
                {showPaper ? <button className="px-4 py-1 rounded-3xl bg-[#039876] text-white text-sm hover:bg-[#038264]" onClick={() => openPaper(speaker.paper)}>View Paper</button> : null}
                {showVideo ? (
                    <button
                        className="h-8 w-8 rounded-full bg-[#039876] text-white text-sm hover:bg-[#038264] flex items-center justify-center"
                        aria-label={`Watch ${speaker.name}'s session video`}
                        title={`Watch ${speaker.name}'s session video`}
                        onClick={() => setActiveVideo({ name: speaker.name, url: speaker.video })}
                    >
                        <FontAwesomeIcon icon={faPlay} />
                    </button>
                ) : null}
            </div>
        );
    };

    // Mobile: coverflow, single card
    const mobileSwiperProps = {
        modules: [EffectCoverflow, Pagination],
        effect: 'coverflow' as const,
        loop: false,
        spaceBetween: 30,
        slidesPerView: 'auto' as const,
        pagination: { clickable: true },
        centeredSlides: true,
        grabCursor: true,
        coverflowEffect: { rotate: 0, slideShadows: false },
        className: "coverflow",
        onActiveIndexChange: (swiper: any) => setActiveIndex(swiper.activeIndex),
    };

    // Desktop: 3 per row, 3 rows, navigation arrows
    const desktopSwiperProps = {
        modules: [Pagination, Navigation, Grid],
        loop: false,
        spaceBetween: 20,
        slidesPerView: 3,
        grid: { rows: 2, fill: 'row' as const },
        pagination: { clickable: true },
        navigation: true,
        grabCursor: true,
        className: "pb-12",
    };

    return (
        <div id="participants" style={{ background: '#039876' }} className="py-6 overflow-x-hidden select-none">
            <div className="pb-12">
                <div className="lg:mx-auto w-full md:mx-[1.5rem]">
                    <h1 className="text-4xl font-bold text-white text-center mb-4">Participants</h1>
                    <div className="text-center mb-4">
                        <button
                            className="px-6 py-2 rounded-3xl bg-white text-[#039876] font-bold hover:bg-gray-100"
                            onClick={() => window.open('https://drive.google.com/drive/folders/1AqcadKF-d9uPZ-R7Ljjo5w5gom6AaJ1RgjShgHHUAFy76E9S8oUWXh-WrK5IDc4lMuHQGKQX?usp=sharing', '_blank')}
                        >
                            Access All Papers
                        </button>
                    </div>

                    {/* Mobile */}
                    <div className="md:hidden">
                        <Swiper {...mobileSwiperProps}>
                            {orderedSpeakers.map((speaker, index) => (
                                <SwiperSlide key={index} style={{ width: "90%" }}>
                                    <div className="bg-white mx-6 rounded-lg mt-16 pb-10">
                                        <h1 className='text-2xl font-bold text-center mt-3 pt-3'>{speaker.name}</h1>
                                        <div className="flex items-center justify-center flex-col">
                                            {speaker.image ? <img src={speaker.image.includes("http") ? speaker.image : `./speakers/${speaker.image}`} alt={speaker.name} className="max-h-52 p-3" /> : null}
                                            {speaker.affiliation ? <p className="font-bold text-xl pb-3 p-6">{speaker.affiliation}</p> : null}
                                            {renderActions(speaker, index === activeIndex)}
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>

                    {/* Desktop: 3 per row */}
                    <div className="hidden md:block px-12">
                        <Swiper {...desktopSwiperProps}>
                            {orderedSpeakers.map((speaker, index) => (
                                <SwiperSlide key={index}>
                                    <div className="bg-white rounded-lg p-4 mt-4 flex flex-col items-center text-center" style={{ minHeight: '320px' }}>
                                        {speaker.image ? <img src={speaker.image.includes("http") ? speaker.image : `./speakers/${speaker.image}`} alt={speaker.name} className="h-40 w-auto object-cover rounded-lg mb-3" /> : null}
                                        <h2 className='text-lg font-bold'>{speaker.name}</h2>
                                        {speaker.affiliation ? <p className="text-gray-500 text-sm mt-1 mb-3">{speaker.affiliation}</p> : null}
                                        {renderActions(speaker)}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>

                </div>
            </div>
            {activeVideo ? (
                <div
                    className="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${activeVideo.name} session video`}
                    onClick={() => setActiveVideo(null)}
                >
                    <div className="w-full max-w-5xl bg-black rounded-lg overflow-hidden shadow-2xl" onClick={(event) => event.stopPropagation()}>
                        <div className="flex items-center justify-between bg-white px-4 py-3">
                            <h2 className="text-lg font-bold text-gray-900">{activeVideo.name}</h2>
                            <button
                                className="h-8 w-8 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center justify-center"
                                aria-label="Close video"
                                onClick={() => setActiveVideo(null)}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <div className="aspect-video bg-black">
                            <iframe
                                title={`${activeVideo.name} session video`}
                                src={activeVideo.url}
                                className="h-full w-full"
                                allow="autoplay; fullscreen"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
